'use client'

import { motion } from 'framer-motion'
import { InfoIcon, SparklesIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  HOME_LIST,
  HOME_ROW,
  HOME_SOFT_SPRING,
  HomePanel,
} from '@/features/home/components/home-motion'
import {
  type Finding,
  type JournalSlice,
  type TradeJournal,
  verdictOf,
} from '@/features/home/lib/home-trade-journal'
import { useNumberFormat } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'

const MIN_TRADES = 5
const MINUTE = 60_000

function Help({ children }: { children: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={children}
          className="text-foreground/30 hover:text-foreground cursor-help transition-colors"
        >
          <InfoIcon className="size-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{children}</TooltipContent>
    </Tooltip>
  )
}

/** A tile: the review's building block, so each part sits in its own frame. */
function Tile({
  title,
  aside,
  className,
  children,
}: {
  title?: string
  aside?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'bg-foreground/[0.035] border-foreground/[0.06] flex min-w-0 flex-col gap-3 rounded-lg border p-4',
        className
      )}
    >
      {title && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-foreground/55 text-xs font-medium">{title}</span>
          {aside}
        </div>
      )}
      {children}
    </div>
  )
}

/**
 * One way of slicing the trades, as rows: label, a bar from a centre zero line (losses to the
 * left in red, gains to the right in green), the amount and how many trades it covers.
 */
function SliceBars<K extends string>({
  title,
  slices,
  labelOf,
}: {
  title: string
  slices: readonly JournalSlice<K>[]
  labelOf: (key: K) => string
}) {
  const t = useTranslations('trade.home.review')
  const fmt = useNumberFormat()
  const peak = Math.max(1, ...slices.map((slice) => Math.abs(slice.pnlUsd)))
  return (
    <Tile title={title}>
      <motion.ul
        variants={HOME_LIST}
        initial="hidden"
        animate="show"
        className="divide-foreground/[0.06] flex flex-col divide-y"
      >
        {slices.map((slice) => {
          const width = `${Math.max(2, (Math.abs(slice.pnlUsd) / peak) * 50)}%`
          return (
            <motion.li
              key={slice.key}
              variants={HOME_ROW}
              className={cn(
                'grid grid-cols-[5.5rem_minmax(0,1fr)_5rem] items-center gap-3 py-2 text-xs first:pt-0 last:pb-0',
                slice.trips === 0 && 'opacity-45'
              )}
            >
              <span className="text-foreground/75 truncate">{labelOf(slice.key)}</span>
              <span className="relative h-2">
                <span className="bg-foreground/[0.07] absolute inset-0 rounded-full" />
                <span className="bg-foreground/25 absolute -inset-y-1 left-1/2 w-px" />
                {slice.trips > 0 && (
                  <motion.span
                    className={cn(
                      'absolute inset-y-0',
                      slice.pnlUsd >= 0
                        ? 'bg-chart-mint left-1/2 rounded-r-full'
                        : 'bg-chart-red right-1/2 rounded-l-full'
                    )}
                    initial={{ width: 0 }}
                    animate={{ width }}
                    transition={HOME_SOFT_SPRING}
                  />
                )}
              </span>
              <span className="flex flex-col items-end leading-tight">
                <span
                  className={cn(
                    'font-medium',
                    slice.trips === 0
                      ? 'text-foreground/40'
                      : slice.pnlUsd >= 0
                        ? 'text-chart-mint'
                        : 'text-chart-red'
                  )}
                >
                  {slice.trips > 0 ? signed(fmt, slice.pnlUsd) : '—'}
                </span>
                <span className="text-foreground/40 text-[10px]">
                  {t('trips', { count: slice.trips })}
                </span>
              </span>
            </motion.li>
          )
        })}
      </motion.ul>
    </Tile>
  )
}

/** A 0–1 meter with an optional reference mark (break-even, 1.0). */
function Meter({
  value,
  mark,
  tone,
}: {
  value: number
  mark?: number
  tone: 'good' | 'bad' | 'neutral'
}) {
  return (
    <span className="bg-foreground/[0.08] relative mt-1 block h-1.5 rounded-full">
      <motion.span
        className={cn(
          'absolute inset-y-0 left-0 rounded-full',
          tone === 'good' ? 'bg-chart-mint' : tone === 'bad' ? 'bg-chart-red' : 'bg-foreground/50'
        )}
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(2, value * 100))}%` }}
        transition={HOME_SOFT_SPRING}
      />
      {mark !== undefined && (
        <span
          className="bg-foreground/70 absolute -inset-y-1 w-0.5 rounded-full"
          style={{ left: `${Math.min(100, Math.max(0, mark * 100))}%` }}
        />
      )}
    </span>
  )
}

function signed(fmt: ReturnType<typeof useNumberFormat>, value: number): string {
  return `${value >= 0 ? '+' : '−'}${fmt.usd(Math.abs(value), { sensitive: true })}`
}

function holdText(t: ReturnType<typeof useTranslations>, ms: number): string {
  if (ms < 90 * MINUTE) return t('minutes', { count: Math.max(1, Math.round(ms / MINUTE)) })
  return t('hours', { count: Math.round((ms / (60 * MINUTE)) * 10) / 10 })
}

function FindingRow({ finding, index }: { finding: Finding; index: number }) {
  const t = useTranslations('trade.home.review')
  const fmt = useNumberFormat()
  const p = finding.params
  const money = (key: string) => fmt.usd(Math.abs(Number(p[key] ?? 0)), { sensitive: true })
  const values: Record<string, string | number> = { ...p }
  for (const key of ['pnl', 'avgWin', 'avgLoss', 'fees']) {
    // The verbs carry the sign ("lost", "paid best"), so amounts are plain.
    if (key in p) values[key] = money(key)
  }
  if ('side' in p) values.side = t(`sidesPlural.${p.side}`)
  if ('bucket' in p) values.bucket = t(`holds.${p.bucket}`)
  if ('session' in p) values.session = t(`sessions.${p.session}`)
  if ('dayMs' in p) {
    values.date = new Date(Number(p.dayMs)).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    })
  }
  return (
    <motion.li
      variants={HOME_ROW}
      className="flex gap-3 py-2.5 text-xs leading-5 first:pt-0 last:pb-0"
    >
      <span
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-medium',
          finding.tone === 'stop'
            ? 'bg-chart-red/15 text-chart-red'
            : finding.tone === 'do'
              ? 'bg-chart-mint/15 text-chart-mint'
              : 'bg-ox-gold/15 text-ox-gold'
        )}
      >
        {index + 1}
      </span>
      <span className="text-foreground/80">
        {t(
          finding.kind === 'worstDay' && Number(p.trips) < 3
            ? 'findings.worstDaySize'
            : `findings.${finding.kind}`,
          values
        )}
      </span>
    </motion.li>
  )
}

/**
 * "Your trading, explained" (Alex's ask, after a friend's Claude-written review of his own
 * history): the last 30 days of fills paired into complete trades, then the plain numbers —
 * how often right, a typical win against a typical loss and what that ratio needs to be,
 * what paid and what leaked by side, hold time and session — and the habits to stop and keep.
 * "Write my full review" hands the same numbers to the assistant for the long-form version.
 */
export function HomeTradingReview({
  journal,
  isPending,
  hasAccounts,
  onWriteReview,
  className,
}: {
  journal: TradeJournal | null
  isPending: boolean
  hasAccounts: boolean
  onWriteReview?: () => void
  className?: string
}) {
  const t = useTranslations('trade.home.review')
  const fmt = useNumberFormat()
  const verdict = journal ? verdictOf(journal) : 'tooFew'
  const enough = journal !== null && verdict !== 'tooFew'

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <>
          {journal && journal.count > 0 && (
            <span className="text-foreground/45 text-xs">
              {t('subtitle', { count: journal.count })}
            </span>
          )}
          {enough && onWriteReview && (
            <Button size="xs" variant="outline" onClick={onWriteReview}>
              <SparklesIcon className="size-3" />
              {t('writeReview')}
            </Button>
          )}
        </>
      }
    >
      {isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : !hasAccounts || !journal ? (
        <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
      ) : !enough ? (
        <p className="text-foreground/50 py-6 text-sm">
          {t('verdict.tooFew', { min: MIN_TRADES })}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {/* The short version: one sentence, and what it came to. */}
            <Tile title={t('sections.summary')}>
              <p className="text-foreground text-[15px] leading-6 tracking-tight text-pretty">
                {t(
                  journal.payoff !== null && journal.payoff < 1
                    ? 'verdict.lossBigger'
                    : 'verdict.winBigger',
                  {
                    winRate: Math.round((journal.winRate ?? 0) * 100),
                    count: journal.count,
                    avgWin: fmt.usd(journal.avgWinUsd ?? 0, { sensitive: true }),
                    avgLoss: fmt.usd(journal.avgLossUsd ?? 0, { sensitive: true }),
                  }
                )}
              </p>
              {/* Where the money went: every winning dollar against every losing one. */}
              <div className="flex flex-col gap-2 text-xs">
                {(['won', 'lost'] as const).map((side) => {
                  const group = journal.trips.filter((trip) =>
                    side === 'won' ? trip.pnlUsd > 0 : trip.pnlUsd < 0
                  )
                  const total = Math.abs(group.reduce((sum, trip) => sum + trip.pnlUsd, 0))
                  const most = Math.max(
                    1,
                    ...(['won', 'lost'] as const).map((other) =>
                      Math.abs(
                        journal.trips
                          .filter((trip) => (other === 'won' ? trip.pnlUsd > 0 : trip.pnlUsd < 0))
                          .reduce((sum, trip) => sum + trip.pnlUsd, 0)
                      )
                    )
                  )
                  return (
                    <div
                      key={side}
                      className="grid grid-cols-[7rem_minmax(0,1fr)_6rem] items-center gap-3"
                    >
                      <span className="text-foreground/55">
                        {t(`totals.${side}`, { count: group.length })}
                      </span>
                      <span className="bg-foreground/[0.07] h-2 rounded-full">
                        <motion.span
                          className={cn(
                            'block h-full rounded-full',
                            side === 'won' ? 'bg-chart-mint' : 'bg-chart-red'
                          )}
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(2, (total / most) * 100)}%` }}
                          transition={HOME_SOFT_SPRING}
                        />
                      </span>
                      <span
                        className={cn(
                          'text-right font-medium',
                          side === 'won' ? 'text-chart-mint' : 'text-chart-red'
                        )}
                      >
                        {side === 'won' ? '+' : '−'}
                        {fmt.usd(total, { sensitive: true })}
                      </span>
                    </div>
                  )
                })}
                {journal.feesUsd > 0 && (
                  <div className="grid grid-cols-[7rem_minmax(0,1fr)_6rem] items-center gap-3">
                    <span className="text-foreground/55">{t('totals.fees')}</span>
                    <span />
                    <span className="text-foreground/70 text-right">
                      −{fmt.usd(journal.feesUsd, { sensitive: true })}
                    </span>
                  </div>
                )}
              </div>
              <div className="border-foreground/[0.08] mt-auto flex items-end justify-between gap-3 border-t pt-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-foreground/50 text-xs">{t('net')}</span>
                  <span
                    className={cn(
                      'font-display text-3xl leading-tight tracking-tight',
                      journal.netPnlUsd >= 0 ? 'text-chart-mint' : 'text-chart-red'
                    )}
                  >
                    {signed(fmt, journal.netPnlUsd)}
                  </span>
                </div>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium',
                    verdict === 'paid'
                      ? 'bg-chart-mint/15 text-chart-mint'
                      : verdict === 'lost'
                        ? 'bg-chart-red/15 text-chart-red'
                        : 'bg-foreground/10 text-foreground/70'
                  )}
                >
                  {t(`verdictChip.${verdict}`)}
                </span>
              </div>
            </Tile>

            {/* The four numbers a coach looks at, each in its own tile. */}
            <dl className="grid grid-cols-2 gap-3">
              <Tile>
                <dt className="text-foreground/55 flex items-center gap-1 text-xs">
                  {t('metrics.winRate')}
                  <Help>{t('metrics.winRateHelp')}</Help>
                </dt>
                <dd className="text-foreground font-display text-2xl leading-none tracking-tight">
                  {Math.round((journal.winRate ?? 0) * 100)}%
                </dd>
                <dd>
                  <Meter value={journal.winRate ?? 0} tone="neutral" />
                  <span className="text-foreground/45 mt-2 block text-[11px]">
                    {t('metrics.winRateSub', { wins: journal.wins, count: journal.count })}
                  </span>
                </dd>
              </Tile>
              <Tile>
                <dt className="text-foreground/55 flex items-center gap-1 text-xs">
                  {t('metrics.payoff')}
                  <Help>{t('metrics.payoffHelp')}</Help>
                </dt>
                <dd
                  className={cn(
                    'font-display text-2xl leading-none tracking-tight',
                    journal.payoff !== null &&
                      journal.breakevenPayoff !== null &&
                      journal.payoff < journal.breakevenPayoff
                      ? 'text-chart-red'
                      : 'text-foreground'
                  )}
                >
                  {journal.payoff !== null
                    ? `${fmt.number(journal.payoff, { maximumFractionDigits: 2 })}×`
                    : '—'}
                </dd>
                {/* A typical win next to a typical loss, to scale. */}
                <dd className="flex flex-col gap-1.5 text-[11px]">
                  {(['win', 'loss'] as const).map((side) => {
                    const amount =
                      side === 'win' ? (journal.avgWinUsd ?? 0) : (journal.avgLossUsd ?? 0)
                    const most = Math.max(journal.avgWinUsd ?? 0, journal.avgLossUsd ?? 0, 1)
                    return (
                      <span
                        key={side}
                        className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-2"
                      >
                        <span className="text-foreground/50">{t(`typical.${side}`)}</span>
                        <span className="flex items-center gap-2">
                          <motion.span
                            className={cn(
                              'h-1.5 rounded-full',
                              side === 'win' ? 'bg-chart-mint' : 'bg-chart-red'
                            )}
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.max(4, (amount / most) * 70)}%` }}
                            transition={HOME_SOFT_SPRING}
                          />
                          <span className="text-foreground/70 shrink-0">
                            {fmt.usd(amount, { sensitive: true })}
                          </span>
                        </span>
                      </span>
                    )
                  })}
                  {journal.breakevenPayoff !== null && (
                    <span className="text-foreground/45">
                      {t('metrics.payoffSub', {
                        needed: fmt.number(journal.breakevenPayoff, { maximumFractionDigits: 2 }),
                      })}
                    </span>
                  )}
                </dd>
              </Tile>
              <Tile>
                <dt className="text-foreground/55 flex items-center gap-1 text-xs">
                  {t('metrics.profitFactor')}
                  <Help>{t('metrics.profitFactorHelp')}</Help>
                </dt>
                <dd
                  className={cn(
                    'font-display text-2xl leading-none tracking-tight',
                    (journal.profitFactor ?? 0) >= 1 ? 'text-foreground' : 'text-chart-red'
                  )}
                >
                  {journal.profitFactor !== null
                    ? fmt.number(journal.profitFactor, { maximumFractionDigits: 2 })
                    : '—'}
                </dd>
                <dd>
                  {/* 0 to 2, with 1 (break-even) marked. */}
                  <Meter
                    value={Math.min(2, journal.profitFactor ?? 0) / 2}
                    mark={0.5}
                    tone={(journal.profitFactor ?? 0) >= 1 ? 'good' : 'bad'}
                  />
                  <span className="text-foreground/45 mt-2 block text-[11px]">
                    {t('metrics.profitFactorSub')}
                  </span>
                </dd>
              </Tile>
              <Tile>
                <dt className="text-foreground/55 flex items-center gap-1 text-xs">
                  {t('metrics.expectancy')}
                  <Help>{t('metrics.expectancyHelp')}</Help>
                </dt>
                <dd
                  className={cn(
                    'font-display text-2xl leading-none tracking-tight',
                    (journal.expectancyUsd ?? 0) >= 0 ? 'text-chart-mint' : 'text-chart-red'
                  )}
                >
                  {signed(fmt, journal.expectancyUsd ?? 0)}
                </dd>
                {journal.medianHoldMs !== null && (
                  <dd className="text-foreground/45 mt-auto text-[11px]">
                    {t('metrics.holdSub', { hold: holdText(t, journal.medianHoldMs) })}
                  </dd>
                )}
              </Tile>
            </dl>
          </div>

          {/* Where it paid and where it leaked. */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SliceBars
              title={t('slices.side')}
              slices={journal.bySide}
              labelOf={(key) => t(`sides.${key}`)}
            />
            <SliceBars
              title={t('slices.hold')}
              slices={journal.byHold}
              labelOf={(key) => t(`holds.${key}`)}
            />
            <SliceBars
              title={t('slices.session')}
              slices={journal.bySession}
              labelOf={(key) => t(`sessions.${key}`)}
            />
          </div>

          {/* The habits, in plain words: what to stop, what to keep. */}
          {journal.findings.length > 0 && (
            <div
              className={cn(
                'grid grid-cols-1 gap-3',
                // Side by side only when there is both something to stop and something to keep.
                journal.findings.some((finding) => finding.tone === 'do') &&
                  journal.findings.some((finding) => finding.tone !== 'do') &&
                  'md:grid-cols-2'
              )}
            >
              {(['stop', 'do'] as const).map((tone) => {
                const items = journal.findings.filter(
                  (finding) => finding.tone === tone || (tone === 'stop' && finding.tone === 'note')
                )
                if (items.length === 0) return null
                return (
                  <Tile
                    key={tone}
                    title={t(`${tone}Title`)}
                    aside={
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          tone === 'stop'
                            ? 'bg-chart-red/15 text-chart-red'
                            : 'bg-chart-mint/15 text-chart-mint'
                        )}
                      >
                        {items.length}
                      </span>
                    }
                  >
                    <motion.ol
                      variants={HOME_LIST}
                      initial="hidden"
                      animate="show"
                      className="divide-foreground/[0.06] flex flex-col divide-y"
                    >
                      {items.map((finding, index) => (
                        <FindingRow
                          key={`${finding.kind}:${String(finding.params.side ?? finding.params.symbol ?? '')}`}
                          finding={finding}
                          index={index}
                        />
                      ))}
                    </motion.ol>
                  </Tile>
                )
              })}
            </div>
          )}
          <p className="text-foreground/35 px-1 text-[11px] leading-4">{t('footnote')}</p>
        </div>
      )}
    </HomePanel>
  )
}
